import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Lock, GameController, Trophy } from "@phosphor-icons/react";
import GameLobby from "@/components/game/GameLobby";
import Game3D from "@/components/game/Game3D";

export default function PadelGame() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null); // { allowed, reason, flag }
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchState, setMatchState] = useState(null); // null | { match_id, player_character, partner_character, opponents, mode }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accessR, charsR] = await Promise.all([
        api.get("/feature-flags/check/padel_game"),
        api.get("/game/characters").catch(() => ({ data: { characters: [] } })),
      ]);
      setAccess(accessR.data);
      setCharacters(charsR.data?.characters || []);
    } catch (e) {
      toast.error("Error cargando juego");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;
  }

  if (!access?.allowed) {
    return (
      <div className="max-w-md mx-auto py-12 text-center" data-testid="padel-game-locked">
        <Lock size={48} weight="duotone" className="text-amber-400 mx-auto mb-4" />
        <h2 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-wider text-white mb-2">Acceso Restringido</h2>
        <p className="text-sm text-gray-400 mb-6">
          {access?.reason === "feature_disabled" && "Esta función está desactivada temporalmente."}
          {access?.reason === "role_not_allowed" && "Tu rol no tiene acceso a esta función todavía. El admin puede activarla."}
          {access?.reason === "feature_not_found" && "Función no encontrada."}
        </p>
        {user?.role === "admin" && (
          <Button onClick={() => window.location.href = "/dashboard/admin-flags"} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase">
            Ir a Feature Flags
          </Button>
        )}
      </div>
    );
  }

  const dydCost = access?.flag?.dyd_cost || 0;

  if (matchState) {
    return (
      <Game3D
        matchState={matchState}
        characters={characters}
        onExit={() => setMatchState(null)}
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="padel-game-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2">
            <GameController size={14} weight="bold" /> Videojuego
          </p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Pádel Arena 3D</h1>
          <p className="text-sm text-gray-400 mt-1">Elige tu avatar, modo de juego y entra a la cancha.</p>
        </div>
        {dydCost > 0 && (
          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs font-bold">
            Costo: {dydCost} DYD por partida
          </Badge>
        )}
      </div>

      <GameLobby
        characters={characters}
        dydCost={dydCost}
        onStartMatch={(state) => setMatchState(state)}
      />
    </div>
  );
}
